import {Component, HostBinding, OnDestroy} from "@angular/core";
import {NotificationData, NotificationService, ReportService} from "@fedeltapointofsale/data-client";
import {TranslateService} from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import {first, Subscription, timer} from "rxjs";
import {AnalyticsService} from "../../analytics/analytics.service";
import {AnalyticEvent} from "../../analytics/analytic-event";

@Component({
    selector: "wbo-notification-chrome-control",
    templateUrl: "./notification-chrome-control.component.html",
    styleUrls: ["./notification-chrome-control.component.scss"],
    providers: [NotificationService]
})
export class NotificationChromeControlComponent implements OnDestroy {

    private static readonly TIME_PULL_UNSEEN_COUNT = 20000;
    private static readonly TIME_PULL_REFRESH = 10000;
    private static readonly TIME_MARK_SEEN = 5000;

    @HostBinding("class.unseen") public unseen: boolean;

    unseenCount: number;

    notifications: NotificationData[] = [];

    private unseenCountSubscription: Subscription;

    private refreshSubscription: Subscription;

    constructor(public notificationService: NotificationService,
                private translateService: TranslateService,
                private reportService: ReportService,
                private analyticsService: AnalyticsService) {
        this.initNextUnseenCountPoll();
    }

    initNextUnseenCountPoll(): void {
        this.unseenCountSubscription = timer(NotificationChromeControlComponent.TIME_PULL_UNSEEN_COUNT).pipe(first()).subscribe(() => {
            this.notificationService.getUnseenCount().then((count) => {
                this.unseenCount = count;
                this.unseen = this.unseenCount != 0;
                this.initNextUnseenCountPoll();
            }).catch((e) => {
                console.log("failed to retrieve unseen count");
                console.log(e);
                this.initNextUnseenCountPoll();
            });
        });
    }

    initNextRefreshPoll(immediately?: boolean): void {
        this.refreshSubscription = timer(immediately ? 0 : NotificationChromeControlComponent.TIME_PULL_REFRESH).pipe(first()).subscribe(() => {
            this.notificationService.searchNotifications().then((notifications) => {
                this.notifications = notifications;
                this.unseen = notifications.filter((notification) => !notification.viewed).length > 0;
                const identifiers: string[] = this.notifications.filter((notification) => !notification.viewed).map((notification) => notification.identifier);
                setTimeout(() => {
                    identifiers.forEach((identifier) => this.notificationService.markAsViewed(identifier).then(() => {
                    }).catch(() => {
                    }));
                }, NotificationChromeControlComponent.TIME_MARK_SEEN);

                this.initNextRefreshPoll();
            }).catch((error) => {
                console.log("Failed to load notifications");
                console.log(error);
                this.initNextRefreshPoll();
            });
        });
    }

    cancelRefreshPoll(): void {
        if (this.refreshSubscription) {
            this.refreshSubscription.unsubscribe();
        }
    }

    handleNotificationAction(notification: NotificationData): void {
        if (notification.type == "report") {
            this.executeReportAction(notification);
        }
    }

    executeReportAction(notification: NotificationData): void {
        this.analyticsService.trackEvent(AnalyticEvent.HEADER_NOTIFICATION_DOWNLOAD_REPORT);
        this.reportService.getReportRequest(notification.data).then((reportRequest) => {
            this.reportService.getReportStream(reportRequest.identifier).then((blob) => {
                const currentDate: Date = new Date();
                FileSaver.saveAs(blob, reportRequest.report + "-" + currentDate.toDateString() + "." + (reportRequest.contentType || "pdf"));
            });
        });
    }

    getMessageParams(notification: NotificationData): void {
        return JSON.parse(notification.messageParams);
    }

    ngOnDestroy(): void {
        if (this.unseenCountSubscription) {
            this.unseenCountSubscription.unsubscribe();
        }
        this.cancelRefreshPoll();
    }

    activeChange(active: boolean): void {
        if (active) {
            this.analyticsService.trackEvent(AnalyticEvent.HEADER_NOTIFICATION);
            this.initNextRefreshPoll(true);
        } else {
            this.cancelRefreshPoll();
            this.notifications = [];
        }
    }

    trackByFn(index, item) {
        return item.identifier;
    }

    clickDiv(event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    deleteNotification(event: MouseEvent, notification: NotificationData): void {
        this.notificationService.deleteNotification(notification.identifier).then(() => {
            this.analyticsService.trackEvent(AnalyticEvent.HEADER_NOTIFICATION_DELETE);
            this.notifications = this.notifications.filter((item) => item.identifier !== notification.identifier);
        });
    }
}
